import { memo } from 'react';
import { LoaderCircleIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Skeleton } from '@rocco/components/skeleton';
import { getUserOccupation } from '~/modules/user';
import type { ContactSuggestion } from '../../types';
import { ParticipantAvatar } from '../ParticipantAvatar';

/**
 * A list of new message contact suggestions in the new message chat box.
 * @see {@link ChatBox}
 */
export function ChatBoxSuggestionsList({
    className,
    suggestions,
    isLoading = false,
    hasMoreSuggestions = false,
    onSuggestionClick,
}: {
    className?: string;
    suggestions: ContactSuggestion[];
    isLoading?: boolean;
    hasMoreSuggestions?: boolean;
    onSuggestionClick: (suggestion: ContactSuggestion) => void;
}) {
    return (
        <div className={cn('flex flex-col gap-[8px]', className)}>
            <div className="mt-[8px] flex items-center gap-[4px] text-[12px] leading-tight text-gray-600">
                Suggestions
                {isLoading && suggestions.length > 0 && (
                    <LoaderCircleIcon className="size-[12px] animate-spin" />
                )}
            </div>
            <div className="flex min-h-0 flex-1 flex-col divide-y divide-white overflow-y-auto">
                {suggestions.length ? (
                    <>
                        {suggestions.map((suggestion) => (
                            <div
                                key={suggestion.ID}
                                className="flex items-center gap-[8px] p-[8px] hover:bg-white/30 focus:bg-white/30"
                                role="button"
                                tabIndex={0}
                                onClick={() => onSuggestionClick(suggestion)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onSuggestionClick(suggestion);
                                    }
                                }}
                            >
                                <ParticipantAvatar
                                    className="size-[36px]"
                                    fallbackClassName="!text-[14px]"
                                    participant={suggestion}
                                />
                                <div className="min-w-0 flex-1">
                                    <div className="truncate text-[14px] font-medium leading-tight">
                                        {suggestion.Name}
                                    </div>
                                    <div className="truncate text-[12px] font-light text-gray-600">
                                        {getUserOccupation(suggestion)}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {hasMoreSuggestions && (
                            <div className="flex items-center justify-center px-[8px] py-[16px] text-[14px] leading-tight text-gray-600">
                                Start typing to view more suggestions
                            </div>
                        )}
                    </>
                ) : isLoading ? (
                    <SuggestionsListSkeleton />
                ) : (
                    <div className="p-[8px] text-center text-[16px] leading-tight text-gray-600">
                        No suggestions found
                    </div>
                )}
            </div>
        </div>
    );
}

const SuggestionsListSkeleton = memo(function SuggestionsListSkeleton() {
    return (
        <>
            {Array.from({ length: 8 }).map((_, index) => (
                <div
                    key={index}
                    className="flex items-center gap-[8px] p-[8px]"
                >
                    <Skeleton className="size-[36px] rounded-full bg-gray-500" />
                    <div className="flex-1 space-y-[4px]">
                        <Skeleton className="h-[14px] w-1/2 bg-gray-500" />
                        <Skeleton className="h-[10px] w-1/6 bg-gray-500" />
                    </div>
                </div>
            ))}
        </>
    );
});
